/**
 * payments.ts
 *
 * API layer for the dual-role Offer Payment History dashboard.
 *
 * Endpoints:
 *  GET /offer-payments/       → paginated payment history list (page, page_size)
 *  GET /offer-payments/csv/   → CSV export of the payment history
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError } from '../utils/apiError';

// ─── API types ───────────────────────────────────────────────────────────────

/** A party (creator/recipient) can come back as a plain string or a nested user object. */
export interface ApiOfferPaymentParty {
  username?: string;
  full_name?: string;
  role?: string;
  avatar_url?: string;
}

export type ApiOfferPaymentPartyField = string | ApiOfferPaymentParty | null | undefined;

/** One row as returned by GET /offer-payments/ */
export interface ApiOfferPaymentHistoryRow {
  id: number;
  transaction_id: string;
  offer_uuid: string;
  offer_title: string;
  gig_uuid: string;
  gig_title: string;
  gig_label: string;
  creator: ApiOfferPaymentPartyField;
  recipient: ApiOfferPaymentPartyField;
  amount: string;
  platform_fee_amount: string;
  processing_fee_amount?: string;
  payer_total_amount?: string;
  provider_payout_amount?: string;
  currency: string;
  status: string;
  role: string;
  payment_channel?: string | null;
  settlement_note?: string | null;
  created_at: string;
}

/** One currency bucket within the list response's `summary` block. */
export interface ApiOfferPaymentSummaryAmount {
  currency?: string;
  amount?: string | number;
}

export interface ApiOfferPaymentHistorySummary {
  total_earned?: ApiOfferPaymentSummaryAmount[];
  total_spent?: ApiOfferPaymentSummaryAmount[];
  pending_payout?: ApiOfferPaymentSummaryAmount[];
}

export interface PaginatedOfferPaymentHistoryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiOfferPaymentHistoryRow[];
  summary?: ApiOfferPaymentHistorySummary;
}

/** Offer Payment charge status, per the `/offer-payments/` `status` filter enum. */
export type OfferPaymentStatusFilter = 'paid' | 'pending';

/** Filters shared by the list and CSV endpoints. */
export interface OfferPaymentsFilterParams {
  search?: string;
  status?: OfferPaymentStatusFilter;
}

export interface OfferPaymentsListParams extends OfferPaymentsFilterParams {
  page?: number;
  page_size?: number;
}

/** UI-friendly row with parsed amounts and flattened party names. */
export interface OfferPaymentHistoryItem {
  id: number;
  transactionId: string;
  offerUuid: string;
  offerTitle: string;
  gigUuid: string;
  gigTitle: string;
  gigLabel: string;
  creator: string;
  creatorAvatar?: string;
  recipient: string;
  recipientAvatar?: string;
  amount: number;
  platformFeeAmount: number;
  processingFeeAmount?: number;
  payerTotalAmount?: number;
  providerPayoutAmount?: number;
  currency: string;
  status: string;
  /** Current user's role in this transaction, as returned by the API (e.g. "earned" | "spent"). */
  role: string;
  /** Lowercased `payment_channel`; anything other than `stripe` was settled off-platform. */
  channel?: string;
  note?: string;
  createdAt: string;
}

/** A summed amount for one currency, as reported by the API's account-wide summary. */
export interface OfferPaymentSummaryAmount {
  currency: string;
  amount: number;
}

export interface OfferPaymentHistorySummary {
  totalEarned: OfferPaymentSummaryAmount[];
  totalSpent: OfferPaymentSummaryAmount[];
  pendingPayout: OfferPaymentSummaryAmount[];
}

export interface PaginatedOfferPaymentsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: OfferPaymentHistoryItem[];
  /** Account-wide totals (not scoped to the current page). */
  summary: OfferPaymentHistorySummary;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseDecimal(value: unknown): number {
  if (value == null || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function parseParty(party: ApiOfferPaymentPartyField): { label: string; avatar?: string } {
  if (party == null || party === '') return { label: '' };
  if (typeof party === 'string') return { label: party };
  const label = party.full_name?.trim() || party.username?.trim() || '';
  return { label, avatar: party.avatar_url || undefined };
}

function mapSummaryAmounts(
  entries: ApiOfferPaymentSummaryAmount[] | undefined,
): OfferPaymentSummaryAmount[] {
  if (!Array.isArray(entries)) return [];
  return entries
    .map((e) => ({ currency: e.currency || '', amount: parseDecimal(e.amount) }))
    .filter((e) => e.currency);
}

function mapSummary(
  summary: ApiOfferPaymentHistorySummary | undefined,
): OfferPaymentHistorySummary {
  return {
    totalEarned: mapSummaryAmounts(summary?.total_earned),
    totalSpent: mapSummaryAmounts(summary?.total_spent),
    pendingPayout: mapSummaryAmounts(summary?.pending_payout),
  };
}

export function mapApiOfferPaymentRow(row: ApiOfferPaymentHistoryRow): OfferPaymentHistoryItem {
  const creator = parseParty(row.creator);
  const recipient = parseParty(row.recipient);
  return {
    id: row.id,
    transactionId: row.transaction_id,
    offerUuid: row.offer_uuid,
    offerTitle: row.offer_title,
    gigUuid: row.gig_uuid,
    gigTitle: row.gig_title,
    gigLabel: row.gig_label,
    creator: creator.label,
    creatorAvatar: creator.avatar,
    recipient: recipient.label,
    recipientAvatar: recipient.avatar,
    amount: parseDecimal(row.amount),
    platformFeeAmount: parseDecimal(row.platform_fee_amount),
    processingFeeAmount:
      row.processing_fee_amount != null && row.processing_fee_amount !== ''
        ? parseDecimal(row.processing_fee_amount)
        : undefined,
    payerTotalAmount:
      row.payer_total_amount != null && row.payer_total_amount !== ''
        ? parseDecimal(row.payer_total_amount)
        : undefined,
    providerPayoutAmount:
      row.provider_payout_amount != null && row.provider_payout_amount !== ''
        ? parseDecimal(row.provider_payout_amount)
        : undefined,
    currency: row.currency,
    status: row.status,
    role: row.role,
    channel: row.payment_channel ? String(row.payment_channel).toLowerCase() : undefined,
    note: row.settlement_note?.trim() || undefined,
    createdAt: row.created_at,
  };
}

// ─── Queries ─────────────────────────────────────────────────────────────────

export const useOfferPaymentsList = (
  params: OfferPaymentsListParams = {},
  options: { enabled?: boolean } = {},
) => {
  return useQuery<PaginatedOfferPaymentsResponse>({
    queryKey: ['offer-payments', 'list', params],
    enabled: options.enabled !== false,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.page_size) searchParams.set('page_size', String(params.page_size));
      if (params.search) searchParams.set('search', params.search);
      if (params.status) searchParams.set('status', params.status);
      const qs = searchParams.toString();
      const { data } = await apiClient.get<PaginatedOfferPaymentHistoryResponse>(
        `/offer-payments/${qs ? `?${qs}` : ''}`,
      );
      const results = Array.isArray(data.results) ? data.results.map(mapApiOfferPaymentRow) : [];
      return {
        count: data.count ?? results.length,
        next: data.next ?? null,
        previous: data.previous ?? null,
        results,
        summary: mapSummary(data.summary),
      };
    },
  });
};

// ─── Mutations ───────────────────────────────────────────────────────────────

/** Export the payment history as CSV, honouring the active filters — GET /offer-payments/csv/ */
export function useOfferPaymentsCsvExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (filters: OfferPaymentsFilterParams = {}) => {
      const response = await apiClient.get('/offer-payments/csv/', {
        params: {
          search: filters.search || undefined,
          status: filters.status || undefined,
        },
        responseType: 'blob',
      });

      const contentType = String(response.headers['content-type'] ?? '');
      if (contentType.includes('application/json') || response.data?.size === 0) {
        throw new Error('Export returned empty or invalid data.');
      }

      return { blob: response.data as Blob };
    },
    onSuccess: ({ blob }) => {
      const date = new Date().toISOString().split('T')[0];
      const filename = `payment_history_${date}.csv`;

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Payment history exported successfully');
      queryClient.invalidateQueries({ queryKey: ['offer-payments'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to export payment history'));
    },
  });
}
